import * as React from 'react';
import { useMemo, useState } from 'react';

import { Archive, Edit } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { RequirePermission, usePermissions } from '@klareospace/pharos-lib-front-utils';
import type { VehicleTemplate } from '@klareospace/pharos-lib-types';
import type { DataTableColumn, RowAction } from '@klareospace/pharos-lib-web-utils';
import {
  createActionsColumn,
  DataTable,
  RowActionsMenu,
  useDataTablePagination,
  VehicleProfileIcon,
} from '@klareospace/pharos-lib-web-utils';

import { resolveVehicleProfileKey } from '../utils/vehicle-profile-label.utils';
import { ArchiveVehicleTemplateDialog } from './archive-vehicle-template-dialog';
import { VehicleTemplateDrawer } from './vehicle-template-drawer';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface VehicleTemplatesTableProps {
  isLoading: boolean;
  vehicleTemplates: VehicleTemplate[];
}

// ─── Component ────────────────────────────────────────────────────────────────

export function VehicleTemplatesTable({ isLoading, vehicleTemplates }: VehicleTemplatesTableProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('settings');
  const { hasPermission } = usePermissions();

  const [editingVehicleTemplate, setEditingVehicleTemplate] = useState<VehicleTemplate | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<string>('');

  const getRowActions = useMemo(
    () =>
      (vehicleTemplate: VehicleTemplate): RowAction[] => {
        const actions: RowAction[] = [
          {
            icon: <Edit className="h-4 w-4" />,
            label: tCommon('actions.edit'),
            onClick: (): void => setEditingVehicleTemplate(vehicleTemplate),
          },
        ];

        if (hasPermission('vehicle-templates:manage') && vehicleTemplate.deletedAt === null) {
          actions.push({
            className: 'text-red-600',
            hasSeparator: true,
            icon: <Archive className="h-4 w-4" />,
            label: tCommon('actions.archive'),
            onClick: (): void => setArchiveTarget(vehicleTemplate.id),
          });
        }

        return actions;
      },
    [hasPermission, t],
  );

  const columns = useMemo<DataTableColumn<VehicleTemplate>[]>(
    () => [
      {
        cell: (vehicleTemplate): React.ReactNode => <span className="font-medium">{vehicleTemplate.name}</span>,
        cellClassName: 'truncate',
        className: 'w-[20%] min-w-[160px]',
        header: tCommon('fields.name'),
        key: 'name',
      },
      {
        cell: (vehicleTemplate): React.ReactNode => (
          <span className="font-mono text-xs text-muted-foreground">{vehicleTemplate.code}</span>
        ),
        cellClassName: 'truncate',
        className: 'w-[15%] min-w-[120px]',
        header: tCommon('fields.codeLabel'),
        key: 'code',
      },
      {
        cell: (vehicleTemplate): React.ReactNode => (
          <div className="flex items-center gap-2">
            <VehicleProfileIcon className="h-4 w-4 text-muted-foreground" profile={vehicleTemplate.profile} />
            <span>{t(resolveVehicleProfileKey(vehicleTemplate.profile))}</span>
          </div>
        ),
        cellClassName: 'truncate',
        className: 'w-[15%] min-w-[140px]',
        header: t('vehicleTemplates.table.columns.profile'),
        key: 'profile',
      },
      {
        cell: (vehicleTemplate): React.ReactNode =>
          `${vehicleTemplate.maxVolumeCapacity.value} ${tCommon(`units.${vehicleTemplate.maxVolumeCapacity.unit}`)}`,
        className: 'w-[15%] min-w-[120px]',
        header: t('vehicleTemplates.table.columns.maxVolume'),
        key: 'maxVolume',
      },
      {
        cell: (vehicleTemplate): React.ReactNode =>
          `${vehicleTemplate.maxWeightCapacity.value} ${tCommon(`units.${vehicleTemplate.maxWeightCapacity.unit}`)}`,
        className: 'w-[15%] min-w-[120px]',
        header: t('vehicleTemplates.table.columns.maxWeight'),
        key: 'maxWeight',
      },
      {
        cell: (vehicleTemplate): React.ReactNode => vehicleTemplate.maxOrderCapacity,
        className: 'w-[10%] min-w-[100px]',
        header: t('vehicleTemplates.table.columns.maxOrders'),
        key: 'maxOrders',
      },
      createActionsColumn<VehicleTemplate>((vehicleTemplate) => (
        <RequirePermission permission="vehicle-templates:manage">
          <RowActionsMenu actions={getRowActions(vehicleTemplate)} ariaLabel={tCommon('table.actionsHeader')} />
        </RequirePermission>
      )),
    ],
    [getRowActions, t],
  );

  const pagination = useDataTablePagination({
    labels: {
      nextPage: tCommon('pagination.nextPage'),
      pageLabel: (current, total): string => tCommon('pagination.pageLabel', { current, total }),
      previousPage: tCommon('pagination.previousPage'),
      showingText: (): string => '',
    },
    pagination: {
      currentPage: 1,
      onPageChange: (): void => {},
      pageSize: vehicleTemplates.length,
      totalItems: vehicleTemplates.length,
      totalPages: 1,
    },
  });

  return (
    <>
      <DataTable<VehicleTemplate>
        ariaLabel={t('vehicleTemplates.table.ariaLabel')}
        columns={columns}
        emptyMessage={t('vehicleTemplates.table.empty')}
        isLoading={isLoading}
        pagination={pagination}
        rowKey={(vehicleTemplate): string => vehicleTemplate.id}
        rows={vehicleTemplates}
      />
      <VehicleTemplateDrawer
        onClose={(): void => setEditingVehicleTemplate(null)}
        open={editingVehicleTemplate !== null}
        vehicleTemplate={editingVehicleTemplate}
      />
      <ArchiveVehicleTemplateDialog onClose={(): void => setArchiveTarget('')} vehicleTemplateId={archiveTarget} />
    </>
  );
}
